import { useEffect } from "react";
import { Link, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { Phone, ChatText, CheckCircle, ArrowRight, MapPin } from "@phosphor-icons/react";
import Navbar from "@/components/site/Navbar";
import ContactFooter from "@/components/site/ContactFooter";
import { PHONE, PHONE_HREF, SMS_HREF } from "@/data/content";
import { BENEFITS } from "@/data/content";
import { CITY_BY_SLUG } from "@/data/cities";
import { useSectionNav } from "@/lib/nav";

const SITE = "https://www.expluscleaning.com";

const ALL_SERVICES = [
  "House & Residential Cleaning",
  "Deep Cleaning",
  "Move-In / Move-Out Cleaning",
  "Office & Commercial Cleaning",
  "Airbnb / Turnover Cleaning",
  "Post-Construction Cleaning",
  "Interior Window Cleaning",
  "Sanitizing",
  "Carpet Odor Removal",
];

export default function CityPage({ slug }) {
  const city = CITY_BY_SLUG[slug];
  const sectionNav = useSectionNav();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!city) return <Navigate to="/" replace />;

  const url = `${SITE}/${city.slug}`;
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "CleaningService",
    "@id": `${url}#business`,
    name: "Explus Cleaning LLC",
    description: `${city.h1} by Explus Cleaning LLC — professional house cleaning, deep cleaning, move-in/move-out, office and commercial cleaning, Airbnb turnovers and more for ${city.name}, WA.`,
    url,
    telephone: "+1-253-534-6626",
    email: "expluscleaning@gmail.com",
    image: `${SITE}/og-image.png`,
    logo: `${SITE}/apple-touch-icon.png`,
    priceRange: "$$",
    sameAs: ["https://maps.app.goo.gl/FPvY5ocQ8LQqRHab9"],
    areaServed: { "@type": "City", name: city.name, addressRegion: "WA", addressCountry: "US" },
    address: { "@type": "PostalAddress", addressRegion: "WA", addressCountry: "US" },
  };

  return (
    <div className="bg-background overflow-x-hidden" data-testid={`city-page-${city.slug}`}>
      <Helmet>
        <title>{city.title}</title>
        <meta name="description" content={city.description} />
        <link rel="canonical" href={url} />
        <meta name="robots" content="index, follow" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Explus Cleaning LLC" />
        <meta property="og:title" content={city.title} />
        <meta property="og:description" content={city.description} />
        <meta property="og:url" content={url} />
        <meta property="og:image" content={`${SITE}/og-image.png`} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={city.title} />
        <meta name="twitter:description" content={city.description} />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <Navbar />

      {/* Hero */}
      <section className="relative min-h-[70vh] flex items-center bg-primary grain" data-testid="city-hero">
        <div className="relative z-10 max-w-5xl mx-auto px-6 md:px-10 pt-28 pb-16 w-full">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-emerald-300 mb-5">
              <MapPin size={16} weight="fill" /> {city.name}, {city.region}
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl font-light tracking-tighter text-white leading-[1.05]">
              {city.h1}
            </h1>
            <p className="mt-6 text-lg text-white/85 max-w-2xl leading-relaxed">{city.intro[0]}</p>

            <div className="mt-9 flex flex-col sm:flex-row gap-4">
              <button onClick={() => sectionNav("#quote")} className="rounded-full bg-white text-primary font-semibold px-8 py-4 min-h-[52px] hover:-translate-y-0.5 hover:shadow-xl transition-all" data-testid="city-quote-btn">
                Get a Free Quote
              </button>
              <a href={PHONE_HREF} className="rounded-full border border-white/70 text-white font-semibold px-8 py-4 min-h-[52px] flex items-center justify-center gap-2 hover:bg-white/10 transition-colors" data-testid="city-call-btn">
                <span className="call-ring"><Phone className="call-shake" size={20} weight="fill" /></span> Call {PHONE}
              </a>
              <a href={SMS_HREF} className="rounded-full border border-white/70 text-white font-semibold px-8 py-4 min-h-[52px] flex items-center justify-center gap-2 hover:bg-white/10 transition-colors" data-testid="city-text-btn">
                <ChatText size={20} weight="fill" /> Text Us
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Intro / local context */}
      <section className="py-16 md:py-24 bg-background">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          <h2 className="font-heading text-3xl md:text-4xl font-medium tracking-tight text-primary">
            Trusted cleaning for {city.name} homes &amp; businesses
          </h2>
          <p className="mt-5 text-foreground/75 text-lg leading-relaxed">{city.intro[1]}</p>
          <p className="mt-4 text-foreground/75 leading-relaxed">{city.local}</p>
          <p className="mt-6 text-foreground/75 leading-relaxed">
            Explus Cleaning LLC is a locally owned, fully insured cleaning company. Learn more{" "}
            <Link to="/#about" className="text-emerald-700 font-medium underline underline-offset-2 hover:text-emerald-800">about us</Link>{" "}
            or explore our{" "}
            <Link to="/#services" className="text-emerald-700 font-medium underline underline-offset-2 hover:text-emerald-800">full list of services</Link>.
          </p>
        </div>
      </section>

      {/* Emphasized services */}
      <section className="py-16 md:py-24 bg-secondary/40">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <h2 className="font-heading text-3xl md:text-4xl font-medium tracking-tight text-primary mb-3">
            Popular cleaning services in {city.name}, WA
          </h2>
          <p className="text-foreground/70 mb-10 max-w-2xl">Here's what {city.name} clients ask for most — though we're glad to build a custom plan for your property.</p>
          <div className="grid sm:grid-cols-3 gap-6">
            {city.emphasis.map((s, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.08 }} className="rounded-2xl border border-border bg-card p-6" data-testid={`city-service-${i}`}>
                <h3 className="font-heading text-xl text-primary">{s.name}</h3>
                <p className="mt-2 text-foreground/70 leading-relaxed text-sm">{s.blurb}</p>
              </motion.div>
            ))}
          </div>

          <div className="mt-10 rounded-2xl border border-border bg-card p-6">
            <h3 className="font-heading text-lg text-primary mb-4">All cleaning services available in {city.name}</h3>
            <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2">
              {ALL_SERVICES.map((s) => (
                <li key={s} className="flex items-center gap-2 text-foreground/75 text-sm">
                  <CheckCircle size={18} weight="fill" className="text-emerald-600 shrink-0" /> {s}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Why choose */}
      <section className="py-16 md:py-24 bg-background">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <h2 className="font-heading text-3xl md:text-4xl font-medium tracking-tight text-primary mb-10">
            Why {city.name} chooses Explus Cleaning LLC
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-10">
            {BENEFITS.map((b) => (
              <div key={b.n} className="border-t border-border pt-5">
                <span className="text-emerald-600 font-semibold text-sm">{b.n}</span>
                <h3 className="font-heading text-xl text-primary mt-2">{b.title}</h3>
                <p className="mt-2 text-foreground/70 leading-relaxed text-sm">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Nearby areas internal links */}
      <section className="py-14 bg-secondary/40">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <h2 className="font-heading text-2xl font-medium text-primary mb-5">Nearby areas we serve</h2>
          <div className="flex flex-wrap gap-3" data-testid="city-nearby">
            <Link to="/" className="rounded-full border border-border bg-card px-4 py-2 text-sm font-medium text-primary hover:border-primary/40 transition-colors">All service areas</Link>
            {city.nearby.map((n) => {
              const target = Object.values(CITY_BY_SLUG).find((c) => c.name === n);
              return target ? (
                <Link key={n} to={`/${target.slug}`} className="rounded-full border border-border bg-card px-4 py-2 text-sm font-medium text-primary hover:border-primary/40 transition-colors">
                  {n}, WA
                </Link>
              ) : null;
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24 bg-primary grain">
        <div className="relative z-10 max-w-3xl mx-auto px-6 md:px-10 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-medium tracking-tight text-white">
            Ready for a cleaner space in {city.name}?
          </h2>
          <p className="mt-4 text-white/80 text-lg">Request your free estimate today and let Explus Cleaning LLC take care of the rest.</p>
          <div className="mt-8 flex flex-col sm:flex-row justify-center gap-4">
            <button onClick={() => sectionNav("#quote")} className="rounded-full bg-white text-primary font-semibold px-8 py-4 min-h-[52px] inline-flex items-center justify-center gap-2 hover:-translate-y-0.5 hover:shadow-xl transition-all" data-testid="city-cta-quote">
              Get a Free Quote <ArrowRight size={18} />
            </button>
            <a href={PHONE_HREF} className="rounded-full border border-white text-white font-semibold px-8 py-4 min-h-[52px] inline-flex items-center justify-center gap-2 hover:bg-white/10 transition-colors" data-testid="city-cta-call">
              <span className="call-ring"><Phone className="call-shake" size={20} weight="fill" /></span> Call Now
            </a>
            <a href={SMS_HREF} className="rounded-full border border-white text-white font-semibold px-8 py-4 min-h-[52px] inline-flex items-center justify-center gap-2 hover:bg-white/10 transition-colors" data-testid="city-cta-text">
              <ChatText size={20} weight="fill" /> Text Us
            </a>
          </div>
        </div>
      </section>

      <ContactFooter />
    </div>
  );
}
